import cx from 'classnames';
import moment from 'moment';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, CountDown, Currency, DivTooltip, Interpolate, Message, TooltipBody } from '@wg/wows-react-uikit';

import settings from '~/settings';
import { t } from '~/helpers/localization';

import StepCSSTransitionGroup from '~/UIKit/StepCSSTransitionGroup/StepCSSTransitionGroup';

import styles from './RegularReward.scss';

import type { RegularRewardRequirements } from '~/Reducers/ReducerTreasury';
import type { IClanMember } from '~/types/declaration';

interface RegularRewardProps {
  isAvailable: boolean;
  haveEnoughMoney: boolean;
  isPending: boolean;
  lastReceive: string | number | null;
  needOil: number;
  members: IClanMember[];
  title: string;
  description: string;
  price: number;
  onBuy: () => void;
  navigateToTreasuryBuilding: () => void;
  index: number;
  offerId: string;
  canDistribute: boolean;
  canBuyNextLevel: boolean;
  requirements: RegularRewardRequirements;
}

export const secToDays = (sec: number): number => {
  return sec / 60 / 60 / 24;
};

export const getNewPlayerNames = (members: IClanMember[]) => {
  return members.filter((member) => member.daysInClan < settings.treasuryMinDaysInClan).map((member) => member.name);
};

const getCooldown = (lastReceive: string | number | null, intervalDays: number) => {
  if (!lastReceive) {
    return [0, 0] as const;
  }

  const lastReceiveMoment = moment.utc(lastReceive);
  const now = moment.utc();
  const msFromReceive = now.diff(lastReceiveMoment);
  const daysFromReceive = now.diff(lastReceiveMoment, 'days');
  const cooldownMs = intervalDays * 24 * 60 * 60 * 1000 - msFromReceive;
  const cooldownDays = intervalDays - daysFromReceive;

  return [cooldownDays, cooldownMs] as const;
};

const RegularReward = ({
  title,
  description,
  price,
  isAvailable,
  haveEnoughMoney,
  onBuy,
  isPending,
  index,
  needOil,
  offerId,
  canDistribute,
  canBuyNextLevel,
  requirements,
  lastReceive,
  navigateToTreasuryBuilding,
}: RegularRewardProps) => {
  const [cooldownDaysState, setCooldownDaysState] = useState(-1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    void cooldownDaysState;
  }, [cooldownDaysState]);

  const interval = useMemo(() => {
    return secToDays(requirements.delay);
  }, [requirements.delay]);

  const [cooldownDays, cooldownMs] = useMemo(() => {
    return getCooldown(lastReceive, interval);
  }, [lastReceive, interval]);

  const hasCooldown = cooldownMs > 0;

  useEffect(() => {
    const fiveMinutesForExample = 300000;

    if (!lastReceive) {
      return;
    }

    if (!(cooldownMs > 0 && cooldownMs < fiveMinutesForExample)) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setCooldownDaysState(cooldownDays);
    }, cooldownMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [lastReceive, cooldownMs, cooldownDays]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const renderBuyButtonTooltip = useCallback(
    (cooldownMsArg: number, cooldownDaysArg: number, haveEnoughMoneyArg: boolean, needOilArg: number) => {
      const hasCooldownLocal = cooldownMsArg > 0;

      if (!haveEnoughMoneyArg) {
        return (
          <TooltipBody>
            <Message type="danger" style="inline">
              <Interpolate
                className={styles.noMoneyError}
                str={t('Не хватает: %(oil)s')}
                oil={<Currency isFlat isBold type="oil" amount={needOilArg} />}
                noWrap
              />
            </Message>
          </TooltipBody>
        );
      }

      if (hasCooldownLocal) {
        return (
          <TooltipBody>
            <Message type="danger" style="inline">
              {cooldownMsArg <= 1 ? (
                <CountDown date={new Date().getTime() + cooldownMsArg} />
              ) : (
                t('Доступен через %(days)s д.', { days: cooldownDaysArg })
              )}
            </Message>
          </TooltipBody>
        );
      }

      return null;
    },
    [],
  );

  return (
    <StepCSSTransitionGroup className={styles.wrapper} level={index}>
      <div className={styles.innerWrapper}>
        <div className={`${styles.header} ${isAvailable && !hasCooldown ? '' : ''}`}>
          <div
            className={cx(styles[`headerBackground-${offerId}`], {
              [styles.headerBackgroundDisabled]: !(isAvailable && !hasCooldown),
            })}
          />
          <div className={styles.title}>{title}</div>

          <div className={styles.interval}>
            {interval ? t('Доступно 1 раз в %(interval)s дней', { interval }) : t('Доступно любое количество')}
          </div>

          {hasCooldown ? (
            <div className={styles.cooldown}>
              {cooldownDays <= 1 ? (
                <CountDown date={new Date().getTime() + cooldownMs} />
              ) : (
                t('Через %(cooldown)s д.', { cooldown: cooldownDays })
              )}
            </div>
          ) : null}
        </div>

        <div className={styles.descr}>{description}</div>

        <div className={styles.price}>
          {t('Стоимость')}
          <Currency isFlat amount={price} color="#fff" isBold type="oil" />
        </div>
      </div>

      {isAvailable && canDistribute ? (
        <div className={styles.buttonRow}>
          <div className={styles.notForAllWrapper}>
            <DivTooltip tooltipBody={renderBuyButtonTooltip(cooldownMs, cooldownDays, haveEnoughMoney, needOil)}>
              <Button isFlat isDisabled={!haveEnoughMoney || isPending || hasCooldown} onClick={onBuy}>
                {t('Приобрести')}
              </Button>
            </DivTooltip>
          </div>
        </div>
      ) : canDistribute ? (
        <div className={styles.warningRow}>
          <Message style="inline" type="error">
            {t('Необходимо улучшить Казначейство')}
          </Message>
          <div className={styles.buttonPlaceholder}>
            {canBuyNextLevel ? (
              <DivTooltip
                tooltipBody={
                  <TooltipBody>{t('Достаточно средств для постройки улучшения. Перейти к постройке.')}</TooltipBody>
                }
              >
                <div className={styles.upgradeTreasuryButton} onClick={navigateToTreasuryBuilding} />
              </DivTooltip>
            ) : null}
          </div>
        </div>
      ) : (
        <div className={cx(styles.warningRow, styles.infoRow)}>
          <Message style="inline" type="none">
            {t('Купить контейнеры могут только командующий и его заместители')}
          </Message>
        </div>
      )}
    </StepCSSTransitionGroup>
  );
};

export default memo(RegularReward);
